// Port de la logique des modificateurs MakeHuman (apps/humanmodifier.py + apps/human.py) :
// HumanParams -> poids de chaque cible.
import type { HumanParams, MacroParams } from './types';
import type { ModifiersFile } from './format';

/** Jeune femme adulte (25 ans), ethnies équilibrées comme le défaut MakeHuman. */
export function defaultParams(): HumanParams {
  return {
    macro: {
      gender: 0, age: 0.5, muscle: 0.5, weight: 0.5, height: 0.5, proportions: 0.5,
      african: 1 / 3, asian: 1 / 3, caucasian: 1 / 3, cupsize: 0.5, firmness: 0.5,
    },
    modifiers: {},
  };
}

export function cloneParams(p: HumanParams): HumanParams {
  return { macro: { ...p.macro }, modifiers: { ...p.modifiers } };
}

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : Number.isFinite(x) ? x : 0.5);

/** Normalise les ethnies pour qu'elles somment à 1 (toutes nulles -> 1/3 chacune). */
export function normalizeEthnics(m: MacroParams): [number, number, number] {
  const a = clamp01(m.african), s = clamp01(m.asian), c = clamp01(m.caucasian);
  const t = a + s + c;
  return t > 1e-6 ? [a / t, s / t, c / t] : [1 / 3, 1 / 3, 1 / 3];
}

/**
 * Valeurs des variables macro (human._setGenderVals, _setAgeVals, …) :
 * femaleVal, youngVal, averagemuscleVal, maxcupVal, …  (clé = valeur sans le suffixe "Val").
 */
export function macroVariables(m: MacroParams): Record<string, number> {
  const v: Record<string, number> = {};
  const g = clamp01(m.gender);
  v.male = g;
  v.female = 1 - g;
  const a = clamp01(m.age);
  if (a < 0.5) {
    v.old = 0;
    v.baby = Math.max(0, 1 - a * 5.333);
    v.young = Math.max(0, (a - 0.1875) * 3.2);
    v.child = Math.max(0, Math.min(1, 5.333 * a) - v.young);
  } else {
    v.child = 0;
    v.baby = 0;
    v.old = Math.max(0, a * 2 - 1);
    v.young = 1 - v.old;
  }
  // weight / muscle : moyenne = 1 - (max + min)
  const tri = (x: number, lo: string, mid: string, hi: string, sumRule: boolean) => {
    x = clamp01(x);
    v[hi] = Math.max(0, x * 2 - 1);
    v[lo] = Math.max(0, 1 - x * 2);
    v[mid] = sumRule ? 1 - (v[hi] + v[lo]) : v[hi] > v[lo] ? 1 - v[hi] : 1 - v[lo];
  };
  tri(m.weight, 'minweight', 'averageweight', 'maxweight', true);
  tri(m.muscle, 'minmuscle', 'averagemuscle', 'maxmuscle', true);
  tri(m.height, 'minheight', 'averageheight', 'maxheight', false);
  tri(m.cupsize, 'mincup', 'averagecup', 'maxcup', false);
  tri(m.firmness, 'minfirmness', 'averagefirmness', 'maxfirmness', false);
  tri(m.proportions, 'uncommonproportions', 'regularproportions', 'idealproportions', false);
  const [af, as, ca] = normalizeEthnics(m);
  v.african = af;
  v.asian = as;
  v.caucasian = ca;
  return v;
}

/**
 * Poids de toutes les cibles actives (poids non nul) pour des paramètres donnés.
 * - Macros : poids = produit des variables dont dépend la cible (getTargetWeights).
 * - UniversalModifier : valeur v dans [min, max] ; v < 0 -> cibles "left" avec -v, v > 0 -> "right" avec v.
 * Les noms de modificateurs inconnus sont ignorés.
 */
export function computeTargetWeights(params: HumanParams, mods: ModifiersFile): Map<string, number> {
  const out = new Map<string, number>();
  const vars = macroVariables(params.macro);
  for (const [name, deps] of mods.macroTargets) {
    let w = 1;
    for (const d of deps) {
      w *= vars[d] ?? 0;
      if (w === 0) break;
    }
    if (Math.abs(w) > 1e-5) out.set(name, (out.get(name) ?? 0) + w);
  }
  const byName = modifierLookup(mods);
  for (const key in params.modifiers) {
    const m = byName.get(key);
    let v = params.modifiers[key];
    if (!m || !Number.isFinite(v) || v === 0) continue;
    v = Math.min(m.max, Math.max(m.min, v));
    const list = v < 0 ? m.left : m.right;
    const w = Math.abs(v);
    for (const t of list) out.set(t, (out.get(t) ?? 0) + w);
  }
  return out;
}

const lookups = new WeakMap<ModifiersFile, Map<string, ModifiersFile['modifiers'][number]>>();
export function modifierLookup(mods: ModifiersFile) {
  let m = lookups.get(mods);
  if (!m) {
    m = new Map(mods.modifiers.map((d) => [d.name, d]));
    lookups.set(mods, m);
  }
  return m;
}
